import { getDeviceHost } from 'src/helpers/interface_helpers';
import { gen2CloudReq } from 'src/services/gen_2_http_reqs';
import { format } from 'src/services/i18n';
import { get_sockets_length, socket_is_host_connected } from 'src/services/socket';
import { get } from 'svelte/store';
import { MAX_CAMERA_STREAMS } from './webrtc-config';
import { CameraRpcError } from './webrtc-utils';

const _format = get(format);

const awaitingCloudMessage = (): string =>
	_format('camera_awaiting_cloud_connection', { default: 'Awaiting cloud connection...' });

/**
 * Make a generic RPC call to the device
 * @param device_id The ID of the device
 * @param method The RPC method to call
 * @param paramsObject The parameters to include in the RPC call
 * @returns The response from the device
 * @throws An error if the RPC call fails
 */
export async function sendCamRPC(device_id: string, method: string, paramsObject?: any) {
	// An existing socket is not enough: a closed one drops the frame and the call
	// would wait out its full timeout for a reply that was never sent.
	if (!get_sockets_length() || !socket_is_host_connected(getDeviceHost(device_id))) {
		// Cloud is still not alive. Backoff and fail.
		await new Promise((resolve) => setTimeout(resolve, 500));
		throw new CameraRpcError(awaitingCloudMessage(), { awaitingCloud: true });
	}
	const response = await gen2CloudReq(device_id, 'camera', method, paramsObject, false, 12000);

	if (response?.error) {
		handleError(response.error);
	}

	return response?.data;
}

/** Device-level codes the cloud reports. */
const KNOWN_ERROR_CODES = ['WRONG_ID', 'TIMEOUT', 'JRPC_ERROR', 'INTERNAL'] as const;

/**
 * Device codes that mean a camera stream slot is gone: taken by an answer the
 * app never received (`500`), or already exhausted (`-108` / `-113`). Callers
 * back off further on these rather than re-requesting a slot the camera has not
 * had time to reclaim.
 */
const SLOT_CONSUMING_CODES = [500, -108, -113];

/**
 * Reasons `socket_send` gives when the app has no cloud socket to send on:
 * none open at all, or none for this device's host. Neither says anything about
 * the device, so both mean the same thing as the pre-flight check in
 * `sendCamRPC` — the cloud connection is not up yet.
 */
const NO_SOCKET_REASONS = ['SOCKET.NO_SOCKETS', 'NO SOCKET FOUND'];
const NO_CLOUD_SOCKET = 'NO_CLOUD_SOCKET';

/**
 * Resolve the code to switch on. It arrives either as a field on an error object
 * or embedded in a message the transport has wrapped, so strings are searched
 * rather than compared. Anything unrecognised keeps its text so the default
 * branch can surface the real reason.
 */
const resolveErrorType = (error: string | any): string | undefined => {
	if (typeof error !== 'string') return error?.error;
	const upper = error.toUpperCase();
	if (NO_SOCKET_REASONS.some((reason) => upper.includes(reason))) return NO_CLOUD_SOCKET;
	return KNOWN_ERROR_CODES.find((code) => upper.includes(code)) ?? upper;
};

// Handle known error codes and return user-friendly messages
// @ToDo: Error handling and messages
// @ToDo: Android camera returns error as object, other API returns string
// @ToDo: Handle device error codes
const handleError = (error: string | any) => {
	// Every branch below assigns this, and none of them let the raw payload
	// through: it is a string the user can read or nothing at all.
	let errorMsg: string;
	let slotConsuming = false;
	let awaitingCloud = false;
	const errorType = resolveErrorType(error);
	switch (errorType) {
		case 'WRONG_ID':
			errorMsg = _format('camera_device_unreachable', {
				default: 'Device is offline or not reachable',
			});
			break;
		// The app has no cloud socket to send on, which says nothing about the camera.
		case NO_CLOUD_SOCKET:
			errorMsg = awaitingCloudMessage();
			awaitingCloud = true;
			break;
		// The request reached the cloud but nothing came back before the deadline.
		case 'TIMEOUT':
			errorMsg = _format('camera_awaiting_network_connection', {
				default: 'Awaiting network connection...',
			});
			break;
		// Raised by the cloud itself, not the camera: the relay failed on its side.
		case 'INTERNAL':
			errorMsg = _format('camera_cloud_communication_error', {
				default: 'Cloud communication error',
			});
			break;
		case 'JRPC_ERROR':
			let deviceError = error?.device_error;
			errorMsg = handleDeviceError(deviceError);
			slotConsuming = SLOT_CONSUMING_CODES.includes(deviceError?.code);
			break;
		default:
			console.warn('[Camera RPC] unrecognised error', error);
			errorMsg = _format('error_unknown', { default: 'Unknown error' });
			break;
	}
	throw new CameraRpcError(errorMsg, { slotConsuming, awaitingCloud });
};

/**
 * Message for a `JRPC_ERROR`, whose payload names the actual fault.
 *
 * @remarks
 * Positive codes come from the camera cloud relaying the call; negative ones are
 * Shelly RPC errors raised by the camera. The code is kept in the text so a
 * support report identifies the case without the user having to describe it.
 */
const handleDeviceError = (response: any) => {
	switch (response?.code) {
		// The cloud gave up waiting on the camera while relaying the call.
		case 500:
			return _format('camera_awaiting_response', {
				default: 'Awaiting camera response ({code})',
				values: { code: '500' },
			});
		case 504:
			return _format('camera_busy', {
				default: 'Camera is busy ({code})',
				values: { code: '504' },
			});
		case 502:
			return _format('camera_busy', {
				default: 'Camera is busy ({code})',
				values: { code: '502' },
			});
		case -113:
			return _format('camera_session_limit_reached', {
				default: 'Camera session limit reached ({code})',
				values: { code: '-113' },
			});
		case -108:
			return _format('camera_stream_limit_reached', {
				default: 'Max {max} streams reached ({code})',
				values: { max: String(MAX_CAMERA_STREAMS), code: '-108' },
			});
		default:
			return _format('camera_error_received', {
				default: 'Received {code}: {message}',
				values: { code: response?.code, message: response?.message },
			});
	}
};
